import React, { useEffect, useState } from "react";
import "../Signup/Signup.scss";
import uploadImage from "../../assets/images/upload-dummy-image.png";
import { Formik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useApi from "../../hook/useAPI";
import { AUTH, USER_MANAGEMENT } from "../../utils/const";
import { getMaxDate, validateImage } from "../../utils/common";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const SignupForm = ({ isAdd, handleClose, onUserCreated }) => {
  const navigate = useNavigate();
  const { postData, postFileData } = useApi();
  const [uploadPhoto, setUploadPhoto] = useState(null);
  const [errorImage, setErrorImage] = useState({});
  const [profileFileName, setProfileFileName] = useState("");

  const initialValues = {
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    dateOfBirth: "",
    gender: "",
  };

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().min(2).max(50).required("First name required"),
    lastName: Yup.string().min(2).max(50).required("Last name required"),
    email: Yup.string()
      .matches(emailRegex, "Invalid email format")
      .required("Email is required"),
    phoneNumber: Yup.string()
      .matches(/^\d{10}$/, "Phone number must be exactly 10 digits")
      .required("Phone number is required"),
    dateOfBirth: Yup.date()
      .max(new Date())
      .required("date of birth is required"),
    gender: Yup.string().oneOf(["male", "female", "other"]).required(),
  });

  const handleSignUp = async (values) => {
    let localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const payload = {
      firstname: values?.firstName,
      lastname: values?.lastName,
      email: values?.email,
      dateofbirth: values?.dateOfBirth,
      phonenumber: values?.phoneNumber,
      gender: values?.gender,
      timezone: localTimeZone,
    };
    if (profileFileName !== "") {
      payload.profileFileName = profileFileName;
    }
    try {
      const response = await postData(`${AUTH.POST_SIGNUP}`, payload);
      if (response.status || response.status === 200) {
        if (isAdd) {
          if (onUserCreated) {
            onUserCreated();
          } else {
            handleClose();
          }
          toast.success("User created successfully");
        } else {
          navigate("/");
          toast.success("User created successfully");
        }
      }
    } catch (error) {
      console.log("Error in Creating User ", error);
      if (
        error.response &&
        error.response.data &&
        error.response.data.message.includes("E11000") &&
        error.response.data.message.includes("email_1 dup key")
      ) {
        toast.error("Email already exists. Please use a different email.");
      } else {
        toast.error("Failed to create user. Please try again.");
      }
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    let imageErrors = {};
    if (file) {
      imageErrors = validateImage(file);
      if (Object.keys(imageErrors).length === 0) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setUploadPhoto(reader.result);
        };
        reader.readAsDataURL(file);
        const formData = new FormData();
        formData.append("profilePic", file);
        try {
          const response = await postFileData(
            `${USER_MANAGEMENT.UPLOAD_PROFILE_PICTURE}`,
            formData
          );
          if (response?.fileName) {
            setProfileFileName(response?.fileName);
          }
        } catch (err) {
          console.error("Error while uploading profile");
        }
      }
    }
    setErrorImage({ ...imageErrors });
  };

  const handleRemoveImage = () => {
    setUploadPhoto(null);
    setProfileFileName("");
  };

  return (
    <div className="create-account-box mb-5">
      <div className="upload-img-btn">
        <label htmlFor="uploadProfile">
          <img
            src={uploadPhoto ? uploadPhoto : uploadImage}
            className="upload_photo img-fluid "
            alt=""
          />

          <input
            type="file"
            id="uploadProfile"
            accept="image/*"
            hidden
            onChange={handleImageChange}
          />
          <h5>Upload Photo</h5>
          {errorImage.image && (
            <div className="upload-photo-error-message">{errorImage.image}</div>
          )}
        </label>

        {uploadPhoto && (
          <button
            aria-label="Close"
            class="btn-close"
            onClick={handleRemoveImage}
          ></button>
        )}
      </div>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSignUp}
      >
        {({
          values,
          touched,
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
        }) => (
          <form>
            <div className="row">
              <div className="col-md-6">
                <div className="form_field_wrapper">
                  <label>First Name</label>
                  <input
                    type="text"
                    value={values.firstName}
                    className="form-control"
                    name="firstName"
                    placeholder="Enter your first name"
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />

                  {errors.firstName && touched?.firstName && (
                    <div
                      className="error-message error-message-height"
                      id="feedback"
                    >
                      {errors.firstName}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-md-6">
                <div className="form_field_wrapper">
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={values.lastName}
                    name="lastName"
                    className="form-control"
                    placeholder="Enter your last name"
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {errors.lastName && touched?.lastName && (
                    <div
                      className="error-message error-message-height"
                      id="feedback"
                    >
                      {errors.lastName}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-md-6">
                <div className="form_field_wrapper">
                  <label>Your email</label>
                  <input
                    type="text"
                    value={values.email}
                    name="email"
                    className="form-control"
                    placeholder="Enter your email"
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {errors.email && touched?.email && (
                    <div
                      className="error-message error-message-height"
                      id="feedback"
                    >
                      {errors.email}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-md-6">
                <div className="form_field_wrapper">
                  <label>Phone Number</label>
                  <input
                    type="text"
                    value={values.phoneNumber}
                    name="phoneNumber"
                    className="form-control"
                    placeholder="Enter your phone number"
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {errors.phoneNumber && touched?.phoneNumber && (
                    <div
                      className="error-message error-message-height"
                      id="feedback"
                    >
                      {errors.phoneNumber}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-md-6">
                <div className="form_field_wrapper">
                  <label>Date of Birth</label>
                    <DatePicker
                      selected={
                        values.dateOfBirth ? new Date(values.dateOfBirth) : null
                      }
                      onChange={(date) => setFieldValue("dateOfBirth", date)}
                      maxDate={new Date()}
                      dateFormat="dd-MM-yyyy"
                      showYearDropdown
                      showMonthDropdown
                      scrollableYearDropdown
                      yearDropdownItemNumber={100}
                      placeholderText="DD/MM/YYYY"
                      className="form-control"
                      showIcon
                    />
                  {errors.dateOfBirth && touched?.dateOfBirth && (
                    <div
                      className="error-message error-message-height"
                      id="feedback"
                    >
                      {errors.dateOfBirth}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-md-6">
                <div className="form_field_wrapper select-half">
                  <label>Gender</label>
                  <select
                    className="form-select"
                    value={values.gender}
                    name="gender"
                    aria-label="Default select example"
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.gender && touched?.gender && (
                    <div
                      className="error-message error-message-height"
                      id="feedback"
                    >
                      {errors.gender}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="create-account-btn mb-4">
              <button type="submit" onClick={handleSubmit}>
                Create Account
              </button>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
};

export default SignupForm;
